import type { ReactNode } from 'react';
import { Building2, Check, ChevronRight, MapPin, Search, User, X } from 'lucide-react';
import type { MyClub } from '../../lib/api/myClubs';
import type { PlayerClubsState } from '../../state/usePlayerClubs';
import { useT } from '../../i18n';
import { MobileSheet, useSheetIds } from './mobileUi';

// ─────────────────────────────────────────────────────────────────────
// THE ACTIVE CLUB CONTEXT, CHOSEN INSIDE THE APP (owner, 2026-10-16).
//
// StarPoint is player-first: the app is the same app with zero, one or many
// memberships, and which club is active is a CONTEXT the 📍 selector sets for
// the features that need one — never a door the player must pass first.
//
// Three pieces, one vocabulary:
//
//   ClubContextList    the choices: no club, each membership, find a club,
//                      and (web only) the organizations this account manages
//   ClubContextSheet   the list as a bottom sheet, opened from the 📍 button
//   ClubContextNeeded  what a club-specific tab shows with no club selected:
//                      the same choices inline, instead of an empty screen
//                      that would read as «you have nothing here»
//
// Selecting grants nothing. The list is `/me/clubs` — roster rows — and the
// server re-checks that row for the club every request names.
// ─────────────────────────────────────────────────────────────────────

/** An organization this account may administer — `/auth/scopes`, not membership. */
export interface ManagedOrganization {
  tenantId: string;
  slug: string;
  name: string;
  kind: string;
}

export interface ClubContextListProps {
  memberships: PlayerClubsState;
  activeClubId: string | null;
  onSelect: (club: MyClub | null) => void;
  /** Offer «no club» as a choice — only meaningful while one is selected. */
  offerNone?: boolean;
  onFindClub: () => void;
  /** Andreas' centre list for the active club's venues. Absent with no club. */
  onOpenCentres?: (() => void) | null;
  organizations?: ManagedOrganization[];
  onEnterOrganization?: (tenantId: string) => void;
}

function Row({
  icon,
  title,
  hint,
  selected = false,
  onClick,
  trailing,
}: {
  icon: ReactNode;
  title: string;
  hint?: string;
  selected?: boolean;
  onClick: () => void;
  trailing?: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={selected ? 'true' : undefined}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-left"
      style={{
        background: selected ? '#f7fdde' : '#fff',
        border: `1px solid ${selected ? '#c2f02f' : '#ecefe8'}`,
      }}
    >
      <span
        className="flex items-center justify-center rounded-2xl flex-shrink-0"
        style={{ width: 36, height: 36, background: '#eef0ea', color: '#0f1611' }}
      >
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[14px] font-semibold text-ink truncate">{title}</span>
        {hint && <span className="block text-[11px] text-ink-3 truncate">{hint}</span>}
      </span>
      {trailing ??
        (selected ? (
          <Check size={16} className="flex-shrink-0" style={{ color: '#65a30d' }} />
        ) : (
          <ChevronRight size={16} className="text-ink-3 flex-shrink-0" />
        ))}
    </button>
  );
}

export function ClubContextList({
  memberships,
  activeClubId,
  onSelect,
  offerNone = true,
  onFindClub,
  onOpenCentres = null,
  organizations = [],
  onEnterOrganization,
}: ClubContextListProps) {
  const t = useT();
  const managed = onEnterOrganization ? organizations : [];
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 px-1">
          {t('mobile.clubContext.yours')}
        </div>
        {offerNone && (
          <Row
            icon={<User size={16} />}
            title={t('mobile.clubContext.none')}
            hint={t('mobile.clubContext.noneHint')}
            selected={activeClubId == null}
            onClick={() => onSelect(null)}
          />
        )}
        {memberships.clubs.map((c) => (
          <Row
            key={c.id}
            icon={<MapPin size={16} />}
            title={c.name}
            selected={c.id === activeClubId}
            onClick={() => onSelect(c)}
          />
        ))}
        {memberships.status === 'loading' && memberships.clubs.length === 0 && (
          <p className="text-[12px] text-ink-3 px-1">{t('mobile.clubContext.loading')}</p>
        )}
        {memberships.status === 'ready' && memberships.clubs.length === 0 && (
          <p className="text-[12px] text-ink-3 px-1">{t('mobile.clubContext.empty')}</p>
        )}
        {memberships.status === 'failed' && (
          <div className="flex items-center justify-between gap-2 px-1">
            <p className="text-[12px] text-ink-3">{t('mobile.clubContext.failed')}</p>
            <button
              type="button"
              onClick={memberships.reload}
              className="text-[12px] font-semibold text-ink underline flex-shrink-0"
            >
              {t('mobile.identity.retry')}
            </button>
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        {onOpenCentres && (
          <Row
            icon={<MapPin size={16} />}
            title={t('mobile.common.centres')}
            hint={t('mobile.profile.centresHint')}
            onClick={onOpenCentres}
          />
        )}
        <Row
          icon={<Search size={16} />}
          title={t('mobile.clubContext.findClub')}
          hint={t('mobile.clubContext.findClubHint')}
          onClick={onFindClub}
        />
      </div>

      {/* THE ORGANIZATION DOOR — where this account may ADMINISTER
          (`/auth/scopes`), which is a different question from where it plays.
          Withheld in the native shell by the caller: the native app has no
          way back from the portal. */}
      {managed.length > 0 && (
        // PHONE ONLY from the rail width up: there «Administrasjon» is its own
        // control in the sidebar (`AdminMenu`), and one door is enough.
        <div className="space-y-1.5 @rail:hidden">
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 px-1">
            {t('mobile.clubContext.adminTitle')}
          </div>
          {managed.map((o) => (
            <Row
              key={o.tenantId}
              icon={<Building2 size={16} />}
              title={o.name}
              onClick={() => onEnterOrganization?.(o.tenantId)}
              trailing={
                <span className="text-[12px] font-semibold text-ink-3 flex-shrink-0">
                  {t('mobile.clubContext.adminEnter')}
                </span>
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** The 📍 selector: the list above, as a bottom sheet over the app. */
export function ClubContextSheet({
  onClose,
  ...list
}: ClubContextListProps & { onClose: () => void }) {
  const t = useT();
  // Focus, Escape and the scrim are `MobileSheet`'s: focus moves in on open,
  // stays inside, and goes back to the 📍 control on close.
  const ids = useSheetIds();
  return (
    <MobileSheet
      labelledBy={ids.title}
      describedBy={ids.lead}
      onClose={onClose}
      className="pb-4 flex flex-col max-h-[85%]"
    >
      <div className="flex items-start justify-between mt-3 mb-3">
        <div>
          <h2
            id={ids.title}
            className="font-display font-semibold text-ink"
            style={{ fontSize: 20 }}
          >
            {t('mobile.clubContext.title')}
          </h2>
          <p id={ids.lead} className="text-[12px] text-ink-3">
            {t('mobile.clubContext.lead')}
          </p>
        </div>
        <button onClick={onClose} aria-label={t('mobile.common.close')} className="p-1 text-ink-3">
          <X size={18} />
        </button>
      </div>
      <div className="overflow-y-auto scrollbar-thin -mx-1 px-1">
        <ClubContextList {...list} />
      </div>
    </MobileSheet>
  );
}

/**
 * A club-specific tab with no club selected.
 *
 * NOT AN EMPTY LIST. «No reservations» would be a statement about the player;
 * the truth is that the app has not been told which club to ask. So it says
 * that, and offers the choice right here rather than sending them elsewhere.
 */
export function ClubContextNeeded(props: Omit<ClubContextListProps, 'offerNone'>) {
  const t = useT();
  return (
    <div className="px-4 pt-2 pb-4 space-y-3">
      <div className="px-1">
        <h1 className="font-display font-semibold text-ink leading-tight" style={{ fontSize: 22 }}>
          {t('mobile.clubContext.neededTitle')}
        </h1>
        <p className="text-[12px] text-ink-3 mt-1">{t('mobile.clubContext.neededLead')}</p>
      </div>
      <ClubContextList {...props} offerNone={false} />
    </div>
  );
}
